<template>
  <v-container class="pt-0">
    <v-form ref="form" v-model="valid">
      <v-card elevation="0" class="d-flex justify-center">
        <v-card-title>Registration</v-card-title>
      </v-card>
      <v-row>
        <v-col cols="12" md="4" sm="6" class="pb-0">
          <label class="form-label"
            >Programme <span class="error--text">* </span></label
          >
          <v-select
            v-model="form.program_id"
            outlined
            :items="programs"
            item-text="program_name"
            item-value="id"
            dense
            :rules="[rules.required]"
            required
            hide-details="auto"
          ></v-select>
        </v-col>
        <v-col cols="12" md="4" sm="6" class="pb-0">
          <label class="form-label"
            >Name (As per the latest Certificate)
            <span class="error--text">* </span></label
          >
          <v-text-field
            v-model.trim="formattedName"
            outlined
            dense
            minlength="3"
            :rules="[rules.required, rules.onlyString]"
            required
            hide-details="auto"
            @input="updateFormattedName"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4" sm="6" class="pb-0">
          <label class="form-label"
            >Date of Birth <span class="error--text">* </span></label
          >
          <v-menu
            v-model="menu"
            :close-on-content-click="false"
            transition="scale-transition"
            max-width="290"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="formattedDob"
                dense
                outlined
                readonly
                placeholder="dd-mm-yyyy"
                :rules="[rules.required]"
                v-bind="attrs"
                v-on="on"
                hide-details="auto"
              />
            </template>
            <v-date-picker
              v-model="form.dob"
              @input="menu = false"
              no-title
              scrollable
              ref="dobPicker"
              :max="sixteenYearsAgo"
              :min="hundredYearsAgo"
            />
          </v-menu>
        </v-col>
        <v-col cols="12" md="4" sm="6" class="pb-0">
          <label class="form-label"
            >Gender <span class="error--text">* </span></label
          >
          <v-radio-group
            v-model="form.gender"
            row
            dense
            :rules="[rules.required]"
            hide-details="auto"
          >
            <v-radio label="Male" value="Male"></v-radio>
            <v-radio label="Female" value="Female"></v-radio>
            <v-radio label="Other" value="Other"></v-radio>
          </v-radio-group>
        </v-col>
        <v-col cols="12" md="4" sm="6" class="pb-0">
          <label class="form-label"
            >Email <span class="error--text">* </span></label
          >
          <v-text-field
            v-model="form.email"
            outlined
            dense
            :rules="[rules.required, rules.email]"
            required
            hide-details="auto"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4" sm="6" class="pb-0">
          <label class="form-label"
            >Mobile Number <span class="error--text">* </span></label
          >
          <template>
            <div class="d-flex align-center">
              <v-autocomplete
                v-model="form.mobile_prefix"
                outlined
                dense
                maxlength="5"
                :items="countryCodes"
                class="mr-2"
                style="max-width: 120px"
                :rules="[rules.required]"
                hide-details
              ></v-autocomplete>

              <v-text-field
                v-model="form.mobile_no"
                outlined
                dense
                :rules="[rules.required, rules.mobileNumber]"
                :maxlength="form.mobile_prefix === '+91' ? 10 : 15"
                required
                hide-details
              ></v-text-field>
            </div>
          </template>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          class="py-0 d-flex justify-center text-caption align-center"
        >
          <v-checkbox
            v-model="form.agree"
            class="mr-2 my-2"
            :rules="[rules.required]"
            hide-details
          ></v-checkbox>
          <span class="mt-2 ml-n2">
            I have read and agree to the
            <a
              :href="rulesLink"
              target="_blank"
              class="text-primary font-weight-medium"
              rel="noopener"
            >
              rules and regulations
            </a>
          </span>
        </v-col>
      </v-row>
      <div class="text-caption mt-4 text-center" v-if="showOtp">
        <div v-if="!otpResent">
          We've sent a one-time password (OTP) to your email address. Please
          check your inbox and enter the OTP below to proceed.
        </div>
        <div v-else>
          We've resent a one-time password (OTP) to your email address.
        </div>

        <v-row class="d-flex justify-center mt-0">
          <v-col cols="12" md="4" sm="6">
            <v-text-field
              v-model="otp"
              outlined
              dense
              maxlength="6"
              :rules="[rules.otp]"
              hide-details="auto"
              class="text-center"
              placeholder="Enter OTP"
            ></v-text-field>
          </v-col>
        </v-row>
        <div class="text-caption" v-if="countdown > 0">
          You can request a new OTP after
          <strong>{{ countdown }}</strong> seconds.
        </div>
      </div>
      <v-row>
        <v-col cols="12" class="d-flex justify-center align-center">
          <v-btn
            v-if="!showOtp"
            :disabled="!valid"
            class="mr-3"
            color="success"
            @click="verify"
          >
            Verify
          </v-btn>
          <v-btn
            v-if="showOtp"
            class="mr-3"
            :disabled="!valid || countdown > 0 || otpResent"
            color="success"
            @click="resendOTP"
          >
            Resend OTP
          </v-btn>
          <v-btn
            :disabled="otp.length !== 6 || !valid"
            :loading="loading"
            color="success"
            @click="proceed"
          >
            Proceed
          </v-btn>
        </v-col>
      </v-row>
      <div class="d-flex justify-center text-caption align-center mt-2">
        Have you registered and would like to complete your payment using the
        unique ID sent via email? &nbsp;
        <router-link to="/applicant-payment">Click Here</router-link>.
      </div>
      <div class="d-flex justify-center text-caption align-center mt-2">
        Already have an application id? &nbsp;
        <router-link to="/home">Click Here</router-link>.
      </div>
    </v-form>
  </v-container>
</template>

<script>
import { rules } from "@/helpers/rules";
import env from "@/helpers/env";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";
export default {
  data() {
    return {
      rules,
      valid: false,
      menu: false,
      form: {
        name: "",
        mobile_prefix: "+91",
      },
      programs: [],
      otp: "",
      showOtp: false,
      countdown: 0, // 5 minutes
      countdownInterval: null,
      otpResent: false,
      countryCodes: [],
      rulesLink: "https://online.sastra.edu/rules-and-regulations",
      loading: false,
      env: env,
    };
  },
  computed: {
    formattedDob() {
      if (!this.form.dob) return "";
      const [year, month, day] = this.form.dob.split("-");
      return `${day}-${month}-${year}`;
    },
    sixteenYearsAgo() {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 16);
      date.setMonth(11);
      date.setDate(31);
      return date.toISOString().substr(0, 10);
    },
    hundredYearsAgo() {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 100);
      date.setMonth(0);
      date.setDate(1);
      return date.toISOString().substr(0, 10);
    },
     formattedName: {
      get() {
        return this.form.name;
      },
      set(value) {
        this.form.name = this.capitalizeWords(value);
      },
    },
  },
  watch: {
    menu(val) {
      if (val) {
        this.$nextTick(() => {
          if (this.$refs.dobPicker) {
            this.$refs.dobPicker.activePicker = "YEAR";
          }
        });
      }
    },
  },
  mounted() {
    this.setSessionStorage();
    localStorage.clear();
    this.getPartner();
    this.getCountryCodes();
  },
  methods: {
    async setSessionStorage() {
      const urlParams = new URLSearchParams(window.location.search);
      const pr = urlParams.get("pr");
      if (pr) {
        sessionStorage.setItem("pr", pr);
      } else {
        const storedPr = sessionStorage.getItem("pr");
        if (storedPr) {
          const newUrl = `${window.location.pathname}?pr=${storedPr}`;
          window.history.replaceState({}, "", newUrl);
        }
      }
    },
    getCountryCodes() {
      const codes = getCountries().map(
        (countryCode) => "+" + getCountryCallingCode(countryCode)
      );
      this.countryCodes = [...new Set(codes)].sort((a, b) => {
        return parseInt(a.slice(1)) - parseInt(b.slice(1));
      });
    },
    verify() {
      this.sendOtp();
      this.showOtp = true;
      this.startCountdown();
    },
    startCountdown() {
      this.countdown = 20;
      clearInterval(this.countdownInterval);
      this.countdownInterval = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
        } else {
          clearInterval(this.countdownInterval);
        }
      }, 1000);
    },
    resendOTP() {
      this.sendOtp();
      if (this.otpResent) return;
      this.otpResent = true;
    },
    async sendOtp() {
      try {
        const res = await this.$axios.post("/otp_verifications", {
          otp_verification: this.form,
        });
        this.$store.commit("showSnackbar", {
          message: "OTP sent successfully",
          color: "success",
        });
      } catch (error) {
        console.error(error);
        this.$store.commit("showSnackbar", {
          message: "Failed to send OTP",
          color: "error",
        });
      }
    },
    async getPartner() {
      try {
        let code = this.$route.query?.pr || sessionStorage.getItem("pr");
        // changed sastra partner applicants to vaagdhara venture llp 
        // if (code == "UC1TQVM=") {
        //   code = "UC1WVkw=";
        // }
        const partnerResponse = code
          ? await this.$axios.get(`/partners/verify_by_code/${code}`)
          : await this.$axios.get("/partners/get_by_code/SUI");
        const programsResponse = await this.$axios.get(
          `/get_partner_scheduled_programs/${partnerResponse.data.data.partner_id}`
        );
        const partner = partnerResponse.data?.data;
        this.form = { ...this.form, ...partner };
        this.programs = programsResponse.data.data;
      } catch (error) {
        const errorMessage = await this.$store.dispatch(
          "throwError",
          error.response
        );
        this.$store.commit("showSnackbar", {
          message: errorMessage,
          color: "error",
        });
      }
    },
    async proceed() {
      this.loading = true;
      const verify_otp = await this.verify_otp_verification();
      if (this.env?.enable_otp ? !verify_otp : this.otp !== "123456") {
        this.$store.commit("showSnackbar", {
          message: "Invalid OTP",
          color: "error",
        });
        this.loading = false;
        return;
      }
      const {
        program_batch_id,
        year,
        batch,
        batch_partner_schedule_id,
        batch_partner_course_fee_id,
        application_fee_amount,
        advance_fee_amount,
        advance_fee_paid,
        application_fee_paid,
      } = this.programs.find((p) => p.id === this.form.program_id);

      this.form = {
        ...this.form,
        program_batch_id,
        year,
        batch,
        batch_partner_schedule_id,
        batch_partner_course_fee_id,
        application_fee_amount,
        advance_fee_amount,
        advance_fee_paid,
        application_fee_paid,
      };
      try {
        const res = await this.$axios.post("/pre_applications", {
          pre_application: this.form,
        });
        this.$store.commit("showSnackbar", {
          message: "OTP verified Successfully",
          color: "success",
        });
        const response = res.data.data;
        if (application_fee_amount == 0 && advance_fee_amount == 0) {
          this.$axios
            .put(
              `/update_pre_application_by_application_id/${response.unique_id}`
            )
            .then((res) => {
              const data = res.data.data;
              this.$router.push({
                path: "/home",
                query: { application_id: data.application_id, dob: this.formattedDob },
              });
            });
        } else {
          const unique_id = response?.unique_id;
          this.$router.push({
            path: "/applicant-payment",
            query: { unique_id },
          });
        }
      } catch (error) {
        this.loading = false;
        const errorMessage = await this.$store.dispatch(
          "throwError",
          error.response
        );
        if (errorMessage === "Email already exists") {
          this.$store.commit("showSnackbar", {
            message: "You have already registered for this programme.",
            color: "error",
          });
        } else {
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        }
      }
    },
    async verify_otp_verification() {
      try {
        const data = await this.$axios.get(
          `/verify_otp/${this.otp}/${this.form.email}`
        );
        return data.data.otp_verification;
      } catch (error) {
        console.log(error);
      }
    },
    capitalizeWords(value) {
      return value.replace(/\w\S*/g, function(txt) {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
      });
    },
    updateFormattedName(value) {
      if (value.length >= 3) {
        this.formattedName = value;
      }
    },
  },
};
</script>

<style scoped>
.form-label {
  font-size: 14px;
}

.v-text-field--filled.v-input--dense.v-text-field--single-line
  > .v-input__control
  > .v-input__slot,
.v-text-field--filled.v-input--dense.v-text-field--outlined
  > .v-input__control
  > .v-input__slot,
.v-text-field--filled.v-input--dense.v-text-field--outlined.v-text-field--filled
  > .v-input__control
  > .v-input__slot,
.v-text-field--full-width.v-input--dense.v-text-field--single-line
  > .v-input__control
  > .v-input__slot,
.v-text-field--full-width.v-input--dense.v-text-field--outlined
  > .v-input__control
  > .v-input__slot,
.v-text-field--full-width.v-input--dense.v-text-field--outlined.v-text-field--filled
  > .v-input__control
  > .v-input__slot,
.v-text-field--outlined.v-input--dense.v-text-field--single-line
  > .v-input__control
  > .v-input__slot,
.v-text-field--outlined.v-input--dense.v-text-field--outlined
  > .v-input__control
  > .v-input__slot,
.v-text-field--outlined.v-input--dense.v-text-field--outlined.v-text-field--filled
  > .v-input__control
  > .v-input__slot {
  min-height: 34px !important;
}

.v-input {
  font-size: 15px;
}
</style>
